import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, GitCompareArrows, CalendarDays, BarChart3, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const DEMO_STEPS = [
  {
    icon: Sparkles,
    color: '#4ABFCF',
    title: 'Your Demo Estimate is Ready',
    desc: 'This is a realistic Corporate LMS estimate with 23 components, standard complexity, and cloud hosting. The results panel on the right shows effort by role, cost breakdown, and a confidence score — all calculated live.',
  },
  {
    icon: GitCompareArrows,
    color: '#3B82F6',
    title: 'Compare Estimates',
    desc: 'Save this estimate, then create a second one (or clone it). Head to Compare to see side-by-side deltas across effort, cost, and every component group.',
    cta: { label: 'Open Compare', path: '/compare' },
  },
  {
    icon: CalendarDays,
    color: '#8B5CF6',
    title: 'Plan the Timeline',
    desc: 'Once saved, open the Planner to turn this estimate into a phased Gantt chart with resource allocation. Use What-If scenarios to explore trade-offs.',
    cta: { label: 'Open Planner', path: '/planner' },
  },
  {
    icon: BarChart3,
    color: '#14B8A6',
    title: 'Cross-Estimate Insights',
    desc: 'As you build more estimates, the Insights dashboard aggregates trends — average cost by complexity, effort distribution by type, and portfolio benchmarks.',
    cta: { label: 'Open Insights', path: '/insights' },
  },
];

export default function QuickStartGuide({ onDismiss }) {
  const [stepIdx, setStepIdx] = useState(0);
  const navigate = useNavigate();
  const step = DEMO_STEPS[stepIdx];

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[340px] animate-in slide-in-from-bottom-4 duration-300" data-testid="quick-start-guide">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-2.5 flex items-center justify-between" style={{ backgroundColor: `${step.color}10` }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${step.color}20` }}>
              <step.icon className="w-3.5 h-3.5" style={{ color: step.color }} />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: step.color }}>
              Quick Start {stepIdx + 1}/{DEMO_STEPS.length}
            </span>
          </div>
          <button onClick={onDismiss} className="text-slate-300 hover:text-slate-500 transition-colors" data-testid="quick-start-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3">
          <h3 className="text-sm font-semibold text-[#0F2040] mb-1">{step.title}</h3>
          <p className="text-[11px] text-slate-500 leading-relaxed">{step.desc}</p>
        </div>

        {/* Footer */}
        <div className="px-4 pb-3 flex items-center justify-between">
          <div className="flex gap-1">
            {DEMO_STEPS.map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === stepIdx ? 'bg-[#4ABFCF]' : 'bg-slate-200'}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step.cta && (
              <Button variant="outline" size="sm"
                className="h-7 text-[10px] px-2.5"
                style={{ borderColor: step.color, color: step.color }}
                onClick={() => navigate(step.cta.path)}
                data-testid={`quick-start-cta-${stepIdx}`}>
                {step.cta.label} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
            {stepIdx < DEMO_STEPS.length - 1 ? (
              <Button size="sm"
                className="h-7 text-[10px] px-3 bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white"
                onClick={() => setStepIdx(i => i + 1)}
                data-testid="quick-start-next">
                Next <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            ) : (
              <Button size="sm"
                className="h-7 text-[10px] px-3 bg-[#0F2040] hover:bg-[#172E5A] text-white"
                onClick={onDismiss}
                data-testid="quick-start-done">
                Got it!
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
